import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import first from 'lodash/first';

import { ISalePackageLinks } from '~/src/api/common-types/sale-package';
import SubscriptionImage from '~/src/components/TariffCard/subscriptions/SubscriptionImage';
import { ISubscriptionBody } from '~/src/components/TariffCard/subscriptions/types';

import * as ST from '~/src/components/TariffCard/styled';


export interface ISubscriptionsProps {
    items: ISubscriptionBody[],
    imageHeight?: number,
    hasBorderRadius?: boolean,
    cardInnerWidth?: number,
    parentWithOneBtn?: number,
    parentWithTwoBtn?: number,
    buttonWidth?: number,
    isZasmotris?: boolean,
    isMega?: boolean,
    salePackageLinks?: ISalePackageLinks[],
    isCottage?: number | boolean,
    className?: string,
}

const Subscriptions = ({
    items = [],
    imageHeight = 30,
    hasBorderRadius = true,
    cardInnerWidth = 256,
    parentWithOneBtn = 252,
    parentWithTwoBtn = 216,
    buttonWidth = 16,
    isZasmotris,
    isMega = false,
    salePackageLinks,
    isCottage,
    className,
}: ISubscriptionsProps): JSX.Element => {
    const innerBlock = useRef<HTMLDivElement>(null);

    const [isVisibleLeftArrow, setIsVisibleLeftArrow] = useState(false);
    const [isVisibleRightArrow, setIsVisibleRightArrow] = useState(false);
    const [subscriptionsBlockWidth, setSubsBlockWidth] = useState(0);
    const [subsParentWidth, setSubsParentWidth] = useState(0);
    const [slideIndex, setSlideIndex] = useState(0);

    const equipItems = useMemo(() => items.filter((item => item.type === 'equipment')), [items]);

    const subscriptionsSort = (a: ISubscriptionBody, b: ISubscriptionBody) => {
        return a.sort && b.sort ? a.sort - b.sort : 0;
    };

    const { uniqueItems, availableSubscriptionsLabel } = useMemo(() => {
        if (!isZasmotris || isCottage || !salePackageLinks?.length) {
            items.sort(subscriptionsSort);
            return {
                uniqueItems: null,
            };
        }

        // Уникальные подписки (напр. в тарифе Засмотрись)
        // @TODO Вынести на верхний уровень (принимать через проп 'items'). Проверить в принципе актуальность такого типа подписок.
        const _subscriptions: ISubscriptionBody[] = salePackageLinks
            .reduce((acc: ISubscriptionBody[], item) => {
                item.subscriptions?.length && item.subscriptions?.forEach(subscription => {
                    const subsIndex = acc.findIndex(el => el.id === subscription.id);
                    if (subsIndex === -1) {
                        acc.push({
                            id: subscription.id,
                            image: subscription.image,
                            imageWebp: subscription.image_webp,
                            width: subscription.image_width,
                            alt: subscription.title,
                            type: 'subscription',
                            sort: subscription.sort,
                        });
                    }
                });
                return acc.sort(subscriptionsSort);
            }, []);

        const subscriptionsLabel = `${first(salePackageLinks)?.subscriptions?.length || 0}/${_subscriptions.length}`;

        return {
            uniqueItems: [..._subscriptions, ...(!isMega ? equipItems : [])],
            availableSubscriptionsLabel: subscriptionsLabel,
        };
    }, [equipItems, isCottage, isMega, isZasmotris, salePackageLinks]);


    useEffect(() => {
        if ((items || uniqueItems) && innerBlock.current) {
            setSubsBlockWidth(innerBlock.current.clientWidth + buttonWidth);

            const parentWidth = (!!subscriptionsBlockWidth && subscriptionsBlockWidth > cardInnerWidth) ? parentWithOneBtn : cardInnerWidth;
            setSubsParentWidth(parentWidth);
        }
    }, [innerBlock.current?.clientWidth, subscriptionsBlockWidth, items, uniqueItems]);

    useEffect(() => {
        if (subscriptionsBlockWidth > subsParentWidth) {
            setIsVisibleRightArrow(true);
        }
    }, [subscriptionsBlockWidth, subsParentWidth]);

    const subscriptionsSlides = useMemo(() => {
        const slides = [0];
        if (subscriptionsBlockWidth && subscriptionsBlockWidth > cardInnerWidth) {
            let decreasingWidth = subscriptionsBlockWidth;
            let currIndex = 1;

            slides.push(parentWithOneBtn);
            decreasingWidth -= parentWithOneBtn;

            if (decreasingWidth > parentWithOneBtn) {
                while (decreasingWidth >= parentWithOneBtn) {
                    slides.push(slides[currIndex] + parentWithTwoBtn);
                    decreasingWidth -= parentWithTwoBtn;
                    currIndex++;
                }
            }
            slides.splice(-1, 1, slides[slides.length - 1] - (parentWithOneBtn - decreasingWidth) + buttonWidth);
        }

        return slides;
    }, [subscriptionsBlockWidth]);

    const changeLeftPosition = useCallback((directionVector: -1 | 1, extremeSlideIndex: number, callback: () => void) => {
        if (subscriptionsSlides && innerBlock.current) {
            innerBlock.current.style.left = -subscriptionsSlides[slideIndex + directionVector] + 'px';

            if (slideIndex + directionVector === extremeSlideIndex) {
                callback();
            }

            setSlideIndex(slideIndex + directionVector);
        }
    }, [slideIndex, subscriptionsSlides]);

    const leftArrowHandler = (e: SyntheticEvent) => {
        e.stopPropagation();
        !isVisibleRightArrow && setIsVisibleRightArrow(true);

        changeLeftPosition(-1, 0, () => {
            setIsVisibleLeftArrow(false);
        });
    };

    const rightArrowHandler = (e: SyntheticEvent) => {
        e.stopPropagation();
        !isVisibleLeftArrow && setIsVisibleLeftArrow(true);

        changeLeftPosition(1, subscriptionsSlides.length - 1, () => {
            setIsVisibleRightArrow(false);
        });
    };

    return (
        <ST.SubsriptionsBlock height={imageHeight} className={className}>
            <ST.LeftArrow
                isVisible={isVisibleLeftArrow}
                onClick={leftArrowHandler}
                data-test='left-arrow'
                className={isVisibleLeftArrow && 'visible'}
                height={imageHeight}
            />

            <ST.Subscriptions
                withLeftBtn={isVisibleLeftArrow}
                withRightBtn={isVisibleRightArrow}
                height={imageHeight}
            >
                <ST.InnerContainer ref={innerBlock}>

                    {availableSubscriptionsLabel &&
                        <ST.SubscriptionLabel>
                            {availableSubscriptionsLabel}
                        </ST.SubscriptionLabel>
                    }
                    {(uniqueItems ?? items).map(subscription => (
                        <SubscriptionImage
                            key={subscription.id}
                            src={subscription.image}
                            webpSrc={subscription.imageWebp}
                            height={imageHeight}
                            width={subscription.width}
                            alt={subscription.alt}
                            hasBorderRadius={hasBorderRadius}
                            data-test='subscription'
                        />
                    ))}
                </ST.InnerContainer>
            </ST.Subscriptions>

            <ST.RightArrow
                isVisible={isVisibleRightArrow}
                onClick={rightArrowHandler}
                data-test='right-arrow'
                height={imageHeight}
            />
        </ST.SubsriptionsBlock>
    );
};

export default Subscriptions;
